import { useState, useEffect } from 'react'
import { useNavigate } from 'react-router-dom'
import { User, Landmark, FileCheck, ArrowRight, ArrowLeft, CheckCircle2, ShieldCheck } from 'lucide-react'
import api from '../api/client'
import { useAuth } from '../auth'

const inp = 'w-full rounded-xl border border-neutral-200 bg-neutral-50 px-3.5 py-2.5 text-sm outline-none focus:border-accent-500 focus:bg-white transition'
const lbl = 'block text-xs font-semibold text-graphite mb-1'

function fileToDataUrl(file) {
  return new Promise((res, rej) => {
    const r = new FileReader()
    r.onload = () => res(r.result)
    r.onerror = rej
    r.readAsDataURL(file)
  })
}

const PASSOS = [
  { n: 1, titulo: 'Dados pessoais', icon: User },
  { n: 2, titulo: 'Bancário e documentos', icon: Landmark },
  { n: 3, titulo: 'Termos e conclusão', icon: FileCheck },
]

export default function CompletarCadastro() {
  const nav = useNavigate()
  const { refreshLicenciado } = useAuth()
  const [passo, setPasso] = useState(1)
  const [f, setF] = useState({
    rg: '', data_nascimento: '', endereco: '', numero: '', bairro: '', cidade: '', estado: '', cep: '',
    banco: '', agencia: '', conta: '', tipo_conta: 'corrente', pix_chave: '',
    titular_nome: '', titular_cpf: '', foto_url: '', documento_foto_url: '', selfie_url: '',
    conjuge_nome: '', conjuge_cpf: '',
  })
  const [aceite, setAceite] = useState(false)
  const [err, setErr] = useState('')
  const [loading, setLoading] = useState(false)

  useEffect(() => {
    api.get('/conta/me').then(({ data }) => {
      const d = data?.licenciado?.dados || {}
      setF((s) => ({ ...s, ...Object.fromEntries(Object.entries(d).filter(([, v]) => v != null && v !== '')) }))
    }).catch(() => {})
  }, [])

  const set = (k) => (e) => setF((s) => ({ ...s, [k]: e.target.value }))
  async function setFile(k, e) {
    const file = e.target.files?.[0]
    if (!file) return
    const url = await fileToDataUrl(file)
    setF((s) => ({ ...s, [k]: url }))
  }

  function validarPasso() {
    if (passo === 1) {
      if (!f.rg || !f.data_nascimento || !f.endereco || !f.cidade || !f.estado || !f.cep)
        return 'Preencha RG, data de nascimento e endereço completo.'
    }
    if (passo === 2) {
      if (!f.banco || !f.agencia || !f.conta || !f.pix_chave)
        return 'Preencha os dados bancários e a chave PIX.'
    }
    return ''
  }

  function avancar() {
    const e = validarPasso()
    if (e) return setErr(e)
    setErr(''); setPasso((p) => Math.min(3, p + 1))
  }

  async function concluir() {
    if (!aceite) return setErr('É necessário aceitar os termos obrigatórios.')
    setErr(''); setLoading(true)
    try {
      const payload = { ...f }
      if (!payload.data_nascimento) delete payload.data_nascimento
      await api.post('/conta/cadastro-complementar', payload)
      await api.post('/conta/termos', { imagem: true, adesao: true, eventos: true })
      await refreshLicenciado()
      nav('/dashboard')
    } catch (ex) {
      setErr(ex?.response?.data?.detail || 'Não foi possível salvar o cadastro.')
    } finally { setLoading(false) }
  }

  return (
    <div className="max-w-2xl mx-auto">
      <div className="mb-5">
        <h1 className="text-xl font-heading font-extrabold text-graphite">Complete seu cadastro</h1>
        <p className="text-sm text-gray-500">Finalize seus dados para liberar todos os recursos da plataforma.</p>
      </div>

      {/* Stepper */}
      <div className="flex items-center gap-2 mb-6">
        {PASSOS.map((p, i) => (
          <div key={p.n} className="flex items-center gap-2 flex-1">
            <div className={`flex items-center gap-2 rounded-xl px-3 py-2 text-sm font-semibold flex-1 ${passo === p.n ? 'bg-accent-500 text-white' : passo > p.n ? 'bg-emerald-50 text-emerald-700 border border-emerald-200' : 'bg-neutral-100 text-neutral-500'}`}>
              {passo > p.n ? <CheckCircle2 size={16} /> : <p.icon size={16} />}
              <span className="hidden sm:inline">{p.titulo}</span>
            </div>
          </div>
        ))}
      </div>

      <div className="bg-white rounded-2xl border border-neutral-200 shadow-sm p-6">
        {err && <div className="mb-4 p-3 rounded-xl bg-red-50 border border-red-200 text-red-700 text-sm">{err}</div>}

        {passo === 1 && (
          <div className="grid sm:grid-cols-2 gap-3">
            <div><label className={lbl}>RG</label><input className={inp} value={f.rg} onChange={set('rg')} /></div>
            <div><label className={lbl}>Data de nascimento</label><input type="date" className={inp} value={f.data_nascimento || ''} onChange={set('data_nascimento')} /></div>
            <div className="sm:col-span-2"><label className={lbl}>Endereço</label><input className={inp} value={f.endereco} onChange={set('endereco')} /></div>
            <div><label className={lbl}>Número</label><input className={inp} value={f.numero} onChange={set('numero')} /></div>
            <div><label className={lbl}>Bairro</label><input className={inp} value={f.bairro} onChange={set('bairro')} /></div>
            <div><label className={lbl}>Cidade</label><input className={inp} value={f.cidade} onChange={set('cidade')} /></div>
            <div><label className={lbl}>Estado (UF)</label><input maxLength={2} className={inp} value={f.estado} onChange={set('estado')} /></div>
            <div><label className={lbl}>CEP</label><input className={inp} value={f.cep} onChange={set('cep')} /></div>
            <div><label className={lbl}>Foto de perfil</label><input type="file" accept="image/*" className={inp} onChange={(e) => setFile('foto_url', e)} /></div>
          </div>
        )}

        {passo === 2 && (
          <div className="grid sm:grid-cols-2 gap-3">
            <div><label className={lbl}>Banco</label><input className={inp} value={f.banco} onChange={set('banco')} /></div>
            <div><label className={lbl}>Tipo de conta</label>
              <select className={inp} value={f.tipo_conta} onChange={set('tipo_conta')}>
                <option value="corrente">Corrente</option><option value="poupanca">Poupança</option>
              </select>
            </div>
            <div><label className={lbl}>Agência</label><input className={inp} value={f.agencia} onChange={set('agencia')} /></div>
            <div><label className={lbl}>Conta</label><input className={inp} value={f.conta} onChange={set('conta')} /></div>
            <div className="sm:col-span-2"><label className={lbl}>Chave PIX (do titular)</label><input className={inp} value={f.pix_chave} onChange={set('pix_chave')} /></div>
            <div><label className={lbl}>Documento com foto (RG/CNH)</label><input type="file" accept="image/*" className={inp} onChange={(e) => setFile('documento_foto_url', e)} /></div>
            <div><label className={lbl}>Selfie (opcional)</label><input type="file" accept="image/*" className={inp} onChange={(e) => setFile('selfie_url', e)} /></div>
            <p className="sm:col-span-2 text-[11px] text-neutral-500">A conta e a chave PIX devem pertencer ao titular do cadastro.</p>
          </div>
        )}

        {passo === 3 && (
          <div className="space-y-4">
            <div className="grid sm:grid-cols-2 gap-3">
              <div><label className={lbl}>Cônjuge / 2º titular (opcional)</label><input className={inp} value={f.conjuge_nome} onChange={set('conjuge_nome')} /></div>
              <div><label className={lbl}>CPF do cônjuge (opcional)</label><input className={inp} value={f.conjuge_cpf} onChange={set('conjuge_cpf')} /></div>
            </div>
            <label className="flex items-start gap-3 rounded-xl border border-neutral-200 p-3 cursor-pointer hover:bg-neutral-50">
              <input type="checkbox" checked={aceite} onChange={(e) => setAceite(e.target.checked)} className="mt-0.5 w-4 h-4 accent-orange-500" />
              <span className="text-sm text-neutral-700 flex items-center gap-1.5">
                <ShieldCheck size={15} className="text-accent-500" />
                Li e aceito eletronicamente os documentos obrigatórios (Direito de Imagem, Termo de Adesão e Política de Eventos).
              </span>
            </label>
          </div>
        )}

        <div className="flex justify-between mt-6">
          <button onClick={() => setPasso((p) => Math.max(1, p - 1))} disabled={passo === 1}
            className="inline-flex items-center gap-1.5 text-sm font-semibold text-neutral-600 disabled:opacity-40 hover:text-graphite">
            <ArrowLeft size={15} /> Voltar
          </button>
          {passo < 3 ? (
            <button onClick={avancar}
              className="inline-flex items-center gap-2 rounded-xl text-white font-bold px-5 py-2.5 hover:brightness-105 transition"
              style={{ background: 'linear-gradient(135deg, #ffcc00, #ff6c00)' }}>
              Continuar <ArrowRight size={15} />
            </button>
          ) : (
            <button onClick={concluir} disabled={loading}
              className="inline-flex items-center gap-2 rounded-xl text-white font-bold px-5 py-2.5 disabled:opacity-60 hover:brightness-105 transition"
              style={{ background: 'linear-gradient(135deg, #ffcc00, #ff6c00)' }}>
              {loading ? 'Salvando...' : 'Concluir cadastro'} <CheckCircle2 size={15} />
            </button>
          )}
        </div>
      </div>
    </div>
  )
}
